import React from 'react';
import './DiagnosticPopup.css';

// Octave-folding frequency to CSS color (mirrors iOS TorusColors.swift)
function frequencyToCSS(freq, resonance = 0) {
  if (!freq || freq <= 0) return 'rgb(40, 40, 40)';
  const hue = ((Math.log2(freq / 80)) % 1.0 + 1.0) % 1.0;
  const sat = 85 + Math.min(resonance / 100, 1.0) * 15;
  return `hsl(${Math.round(hue * 360)}, ${Math.round(sat)}%, 60%)`;
}

const DiagnosticPopup = ({ data, position, onClose }) => {
  if (!data) return null;

  const popupStyle = {
    position: 'fixed',
    top: '50%',
    right: '20px',
    transform: 'translateY(-50%)',
    maxHeight: '70vh',
    overflowY: 'auto',
    width: '340px',
    backgroundColor: 'rgba(20, 20, 30, 0.95)',
    color: 'white',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    backdropFilter: 'blur(10px)',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)'
  };

  // Get metric from data (supports both old and new format)
  const getMetric = () => {
    const index = data.index;

    // New local analysis format: data.metrics_42[index]
    if (data.metrics_42 && data.metrics_42[index]) {
      return { ...data.metrics_42[index], available: true };
    }

    // Inline metric (already extracted)
    if (data.frequency !== undefined) {
      return { ...data, available: true };
    }

    // Old format fallback
    if (data.features || data.analysis?.features) {
      const features = data.features || data.analysis?.features || {};
      return {
        id: index + 1,
        name: `Segment ${index + 1}`,
        frequency: features.F0_mean || features.f0_mean || 0,
        note: '-',
        volume: (features.energy || 0) * 100,
        resonance: 0,
        value: `${(features.F0_mean || features.f0_mean || 0).toFixed(0)} Hz`,
        unit: 'Hz',
        description: '',
        performance: 'neutral',
        available: true
      };
    }

    return {
      id: index + 1,
      name: `Segment ${index + 1}`,
      frequency: 0,
      note: '-',
      volume: 0,
      resonance: 0,
      value: 'N/A',
      unit: '',
      description: '',
      performance: 'unavailable',
      available: false
    };
  };

  const metric = getMetric();
  const metricColor = frequencyToCSS(metric.frequency, metric.resonance);

  const performanceLabel = {
    excellent: { text: 'Excellent', color: '#00ff88', icon: '' },
    good: { text: 'Bon', color: '#44ff44', icon: '' },
    neutral: { text: 'Normal', color: '#ffaa44', icon: '' },
    warning: { text: 'Faible', color: '#ff6644', icon: '' },
    unavailable: { text: 'Silence', color: '#666', icon: '' }
  }[metric.performance] || { text: '-', color: '#666', icon: '' };

  return (
    <div className="diagnostic-popup" style={popupStyle}>
      <button
        className="popup-close"
        onClick={onClose}
        style={{
          position: 'absolute', top: '10px', right: '10px',
          color: 'white', backgroundColor: 'transparent',
          border: 'none', fontSize: '24px', cursor: 'pointer',
          width: '30px', height: '30px',
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}
      >&times;</button>

      {/* Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{
            width: '4px', height: '30px',
            backgroundColor: metricColor,
            borderRadius: '2px'
          }} />
          <h3 style={{ margin: 0, fontSize: '18px', color: 'white' }}>
            Segment {(data.index || 0) + 1}
          </h3>
        </div>
        {metric.description && (
          <div style={{ marginLeft: '14px', marginTop: '4px', fontSize: '12px', color: '#888' }}>
            {metric.description}
          </div>
        )}
      </div>

      {/* Frequency + Note */}
      <div style={{
        backgroundColor: 'rgba(0, 0, 0, 0.3)',
        borderRadius: '8px',
        padding: '16px',
        marginBottom: '12px',
        textAlign: 'center'
      }}>
        <div style={{
          fontSize: '40px', fontWeight: 'bold',
          color: metric.frequency > 0 ? metricColor : '#666',
          fontFamily: 'monospace'
        }}>
          {metric.frequency > 0 ? `${Math.round(metric.frequency)}` : '-'}
        </div>
        <div style={{ fontSize: '16px', color: '#999' }}>
          {metric.frequency > 0 ? `Hz  (${metric.note || '-'})` : 'Silence'}
        </div>
      </div>

      {/* Volume + Resonance bars */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <MetricBar label="Volume" value={metric.volume || 0} max={50} unit="%" color="#43e97b" />
        <MetricBar label="Resonance" value={metric.resonance || 0} max={100} unit="%" color="#fa709a" />
      </div>

      {/* Performance */}
      {metric.available && metric.performance !== 'unavailable' && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '10px',
          padding: '10px 12px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          borderRadius: '8px',
          borderLeft: `3px solid ${performanceLabel.color}`
        }}>
          <span style={{ fontSize: '16px' }}>{performanceLabel.icon}</span>
          <div>
            <div style={{ fontSize: '11px', color: '#888' }}>Performance</div>
            <div style={{ fontSize: '13px', color: performanceLabel.color, fontWeight: 'bold' }}>
              {performanceLabel.text}
            </div>
          </div>
        </div>
      )}

      {/* Footer */}
      <div style={{
        marginTop: '14px', paddingTop: '10px',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        fontSize: '11px', color: '#555', textAlign: 'center'
      }}>
        Ligne {(data.index || 0) + 1}/42
      </div>
    </div>
  );
};

// Horizontal bar for volume/resonance
const MetricBar = ({ label, value, max, unit, color }) => {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div style={{ flex: 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#888', marginBottom: '4px' }}>
        <span>{label}</span>
        <span style={{ color }}>{value.toFixed(1)}{unit}</span>
      </div>
      <div style={{ height: '4px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          backgroundColor: color,
          borderRadius: '2px',
          transition: 'width 0.3s'
        }} />
      </div>
    </div>
  );
};

export default DiagnosticPopup;
